import {AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {Router} from '@angular/router';
import {MatDialogRef} from '@angular/material/dialog';
import {NgxScannerQrcodeComponent, ScannerQRCodeConfig, ScannerQRCodeResult} from 'ngx-scanner-qrcode';
import {MatExpansionPanel} from '@angular/material/expansion';
import {BehaviorSubject, lastValueFrom, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Bulto} from "../interfaces/Bulto";
import {UtilitiesService} from "../services/utilities.service";
import {PickupService} from "../services/pickup.service";
import {TempStateService} from "../services/temp-state.service";
import {StatusLogService} from "../services/status-log.service";

interface CameraInfo {
  id: string;
  label: string;
  humanLabel: string;
  metaId: number;
  position?: 'front' | 'back' | 'unknown';
}

interface UbicacionConBultos {
  escaneada: boolean;
  bultos: Bulto[];
  isChecked: boolean;
}

@Component({
  selector: 'app-scanner-modal-entrega',
  templateUrl: './scanner-modal-entrega.component.html',
  styleUrls: ['./scanner-modal-entrega.component.scss']
})
export class ScannerModalEntregaComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('action') scanner!: NgxScannerQrcodeComponent;
  @ViewChildren(MatExpansionPanel) expansionPanels!: QueryList<MatExpansionPanel>;

  lockUserInteraction: boolean = true;
  retryButton: boolean = false;
  retryingCameras: boolean = false;
  private scannerInitialized = false;

  activeCameraId: string | null = null;
  scannerConfig: ScannerQRCodeConfig = {
    fps: 30,
    isBeep: false,
    vibrate: 300,
    constraints: {
      audio: false,
      video: {
        width: window.innerWidth,
        aspectRatio: 4 / 3
      } as MediaTrackConstraints
    }
  };

  timeout: any;

  cameras: CameraInfo[] = [];
  status = new BehaviorSubject<{
    message: string;
    type: 'success' | 'error' | 'loading';
  }>({message: 'Buscando codigo QR...', type: 'loading'});

  bultosPorUbicacion: Record<string, UbicacionConBultos> = {};
  permiteEntrega = false;

  private destroy$ = new Subject<void>();
  public currentStatus : {message: string, type: 'success' | 'error' | 'loading'} = {message: 'Buscando código QR...', type: 'loading'};

  constructor(
    private router: Router,
    private dialogRef: MatDialogRef<ScannerModalEntregaComponent>,
    private pickupService: PickupService,
    private utilityService: UtilitiesService,
    private tempStateService: TempStateService,
    private statusService: StatusLogService
  ) {
    this.status.subscribe(status => {
      this.currentStatus = status;
    });
  }

  ngOnInit() {
    this.lockUserInteraction = true;
  }

  async ngAfterViewInit() {
    this.retryButton = false;
    this.retryingCameras = false;

    this.initializeBultos();

    this.activateCameras();
  }

  async activateCameras() {
    this.retryingCameras = true;

    try {
      await this.initializeCameras();

      if (this.scanner) {
        try {
          console.log('Device Active ID:', this.scanner.deviceIndexActive);
          await lastValueFrom(this.scanner.start());
          this.scannerInitialized = true;
          this.setupScannerSubscription();
          console.log('Scanner initialized successfully');
        } catch (error) {
          console.error('Error starting scanner:', error);
          this.showStatus('Error al iniciar el escáner', 'error');
          location.reload();
          this.retryButton = true;
        }
      }
    } catch (error) {
      console.error('Error during camera initialization:', error);
      location.reload();
      this.showStatus('No se pudo acceder a las cámaras', 'error');
      this.retryButton = true;
    } finally {
      this.retryingCameras = false;
    }
  }

  async selectCamera(device: CameraInfo) {
    try {
      if (this.lockUserInteraction) return;

      if (this.scannerInitialized && this.scanner?.isStart) {
        try {
          await lastValueFrom(this.scanner.stop());
        } catch (error) {
          console.error('Error stopping previous camera:', error);
        }
      }

      this.activeCameraId = device.id;

      const videoConstraints = {
        ...(this.scannerConfig.constraints?.video as MediaTrackConstraints || {}),
        deviceId: {exact: device.id}
      };

      if (this.scannerConfig.constraints) {
        this.scannerConfig.constraints.video = videoConstraints;
      }

      await lastValueFrom(this.scanner.playDevice(device.id));

      this.scanner.deviceIndexActive = device.metaId;

      this.showStatus('Iniciando cámara...', 'loading');

    } catch (error) {
      this.showStatus('Error al iniciar la cámara', 'error');
      console.error('Error in camera selection:', error);
    }
  }

  verificarEntrega(bulto: Bulto | null = null): void {
    if (bulto) this.tempStateService.updateBultos(bulto.sg123, bulto)

    const todasLasUbicacionesCompletas = Object.values(this.bultosPorUbicacion)
      .every(ubicacion => ubicacion.bultos.filter(bulto => bulto.ubicacion).every(bulto => bulto.isChecked));

    const todasLasUbicacionesEscaneadas = Object.keys(this.bultosPorUbicacion)
      .filter(keyUbicacion => keyUbicacion !== 'sin_ubicacion')
      .every(keyUbicacion => {
        return this.bultosPorUbicacion[keyUbicacion].escaneada;
      });

    this.permiteEntrega = todasLasUbicacionesCompletas;

    if (this.permiteEntrega) {
      this.statusService.changePedidoStatus(this.pickupService.pedidoActivo, 4);
      this.showStatus('✅ Todo listo para continuar', 'success');
    } else if (!todasLasUbicacionesCompletas) {
      this.statusService.changePedidoStatus(this.pickupService.pedidoActivo, 3);
      this.showStatus('Debes seleccionar todos los bultos', 'error');
    }
  }

  verificarTodasLasUbicacionesCheckeadas(ubicacion: UbicacionConBultos): boolean {
    return ubicacion.bultos.filter(bulto => bulto.ubicacion).every(bulto => bulto.isChecked);
  }

  async continueToDelivery(): Promise<void> {
    if (this.lockUserInteraction) return;

    this.lockUserInteraction = true;

    try {
      if (this.scannerInitialized && this.scanner?.isStart) {
        try {
          await lastValueFrom(this.scanner.stop());
        } catch (error) {
          console.error('Error stopping scanner before navigation:', error);
        }
      }
      this.utilityService.stopAllActiveCameras();
    } catch (error) {
      console.error('Error during cleanup before navigation:', error);
    } finally {
      this.dialogRef.close(true);
      this.router.navigate(['/opciones-entrega']);
    }
  }

  async cancel(): Promise<void> {
    // if (this.lockUserInteraction) return;

    this.lockUserInteraction = true;

    try {
      console.log('Canceling scanner... ' + this.scanner?.isStart);
      if (this.scanner?.isStart) {
        try {
          await lastValueFrom(this.scanner.stop());
        } catch (error) {
            console.error('Error stopping scanner during cancel:', error);
        }
      }else{
        this.utilityService.stopAllActiveCameras();
      }
    } catch (error) {
      console.error('Error during cancel operation:', error);
    } finally {
      this.dialogRef.close(false);
    }
  }

  ngOnDestroy() {
    console.log('Component destroying, running cleanup...');
    this.destroy$.next();
    this.destroy$.complete();
    this.cancel();
    clearTimeout(this.timeout);
  }

  private initializeBultos(): void {
    if (!this.pickupService.pedidoActivo?.bultos) {
      console.error('No hay bultos activos');
      return;
    }

    this.bultosPorUbicacion = this.pickupService.pedidoActivo.bultos.reduce((acc, bulto) => {
      const ubicacion = bulto.ubicacion || 'sin_ubicacion';
      if (!acc[ubicacion]) {
        acc[ubicacion] = {escaneada: false, bultos: [], isChecked: false};
      }
      acc[ubicacion].bultos.push(bulto);
      return acc;
    }, {} as Record<string, UbicacionConBultos>);

    console.log(this.bultosPorUbicacion);
  }

  private async initializeCameras() {
    try {
      this.status.next({message: 'Buscando cámaras disponibles...', type: 'loading'});
      const devices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      this.retryingCameras = false;

      if(this.pickupService.cachedCameras && this.pickupService.cachedCameras.length){
        if(this.pickupService.cachedCameras.every((cachedCamera) => videoDevices.find((device) => device.deviceId === cachedCamera))){
          if(this.pickupService.cachedCameras.length < videoDevices.length) {
            this.pickupService.cachedCameras = videoDevices.map((device: MediaDeviceInfo) => device.deviceId);
          }
        }else{
          this.showStatus('No se pudo acceder a las cámaras', 'error');
          return this.retryButton = true;
        }
      }else{
        this.showStatus('No se pudo acceder a las cámaras', 'error');
        return this.retryButton = true;
      }

      this.cameras = videoDevices.map((device: MediaDeviceInfo, index: number) => ({
        id: device.deviceId,
        metaId: index,
        label: device.label,
        humanLabel: this.getHumanReadableLabel(device.label),
        position: this.getCameraPosition(device.label)
      }));

      this.cameras.sort((a, b) => {
        if (a.position === 'back') return -1;
        if (b.position === 'back') return 1;
        return 0;
      });

      const backCamera: CameraInfo | undefined = this.cameras.find(cam => cam.position === 'back');

      if (backCamera) {
        this.scanner.deviceIndexActive = backCamera.metaId;
        await lastValueFrom(this.scanner.playDevice(backCamera.id));
      } else if (this.cameras.length > 0) {
        this.scanner.deviceIndexActive = this.cameras[0].metaId;
        await lastValueFrom(this.scanner.playDevice(this.cameras[0].id));
      } else {
        this.showStatus('No se encontraron cámaras', 'error');
        return;
      }

      this.showStatus('Cámaras listas', 'success');
      this.verificarEntrega();
      if (!this.permiteEntrega) this.showStatus('Buscando código QR...', 'loading');
      this.lockUserInteraction = false;
    } catch (error) {
      this.lockUserInteraction = false;
      this.showStatus('No se pudo acceder a las cámaras', 'error');
      console.error('Error accessing cameras:', error);
    }
  }

  private getHumanReadableLabel(label: string): string {
    const labelLower = label.toLowerCase();
    if (labelLower.includes('back') || labelLower.includes('trasera')) {
      return '📸 Cámara Trasera';
    } else if (labelLower.includes('front') || labelLower.includes('frontal')) {
      return '🤳 Cámara Frontal';
    }
    return '📱 Otra Cámara';
  }

  private getCameraPosition(label: string): 'front' | 'back' | 'unknown' {
    const labelLower = label.toLowerCase();
    console.log(labelLower);
    if (labelLower.includes('back') || labelLower.includes('trasera')) {
      return 'back';
    } else if (labelLower.includes('front') || labelLower.includes('frontal')) {
      return 'front';
    }
    return 'unknown';
  }

  private setupScannerSubscription(): void {
    this.scanner.data.pipe(
      takeUntil(this.destroy$)
    ).subscribe(results => {
      if (results?.length > 0) {
        this.handleScan(results);
      }
    });
  }

  private handleScan(results: ScannerQRCodeResult[]) {
    if (!results?.[0]?.value) return;

    const scannedValue = results[0].value;

    if (!scannedValue.includes('-')) {
      this.showStatus('El código debe contener guiones (ejemplo: A-1-B-2)', 'error');
      return;
    }

    if (!this.isValidQRFormat(scannedValue)) {
      this.showStatus('Formato inválido: Debe tener 4 caracteres separados por guiones', 'error');
      return;
    }

    const location = scannedValue.replace('-', '');

    if (!this.bultosPorUbicacion[location]) {
      this.showStatus(`Ubicación "${location}" no encontrada`, 'error');
      return;
    }

    this.processScan(location);
  }

  private processScan(location: string): void {
    this.bultosPorUbicacion[location].escaneada = true;
    this.openLocationPanel(location);
    this.verificarEntrega();

    this.showStatus(`✅ Ubicación ${location} escaneada`, 'success');

    if (navigator.vibrate) {
      navigator.vibrate(200);
    }
  }

  private isValidQRFormat(code: string): boolean {
    return /^[a-zA-Z0-9]{1,2}-[a-zA-Z0-9]{1,2}-[a-zA-Z0-9]{1,2}-[a-zA-Z0-9]{1,2}$/.test(code);
  }

  private openLocationPanel(location: string): void {
    const index = Object.keys(this.bultosPorUbicacion).indexOf(location);
    if (index !== -1) {
      const panel = this.expansionPanels.get(index);
      if (panel && !panel.expanded) {
        panel.open();
      }
    }
  }

  private showStatus(message: string, type: 'success' | 'error' | 'loading'): void {
    this.status.next({message, type});
  }
}